import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { BillingDocumentService } from '../../Modules/Billing/Services/billing-document.service';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { ExternalLinkService } from '../../Modules/Shared/Services/external-link.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import {
  CREDIT_NOTE_ALREADY_RECORDED_MESSAGE,
  CREDIT_NOTE_CREATED_MESSAGE,
  INVOICE_RESENT_MESSAGE,
  INVOICE_UNCOLLECTIBLE_MESSAGE,
  ROLE_DOCUMENT_SEND,
  ROLE_MONEY_OUT,
} from '../../Consts/billing-documents.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { InvoiceDetail } from '../../Models/backoffice/invoice-detail.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { OrganizationShowResponse } from '../../Interfaces/api/backoffice/organization.interface';
import {
  CreditNotePayload,
  InvoiceShowResponse,
} from '../../Interfaces/api/backoffice/billing-document.interface';

@Injectable()
export class InvoiceDetailFacade {
  private readonly _documentService = inject(BillingDocumentService);
  private readonly _organizationService = inject(OrganizationService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _externalLink = inject(ExternalLinkService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _organizationId = signal<string>('');
  private readonly _invoiceId = signal<string>('');
  private readonly _isMutating = signal<boolean>(false);

  private readonly _organization = httpResource<LaravelDefaultResponse<OrganizationShowResponse>>(
    () =>
      this._organizationId()
        ? this._organizationService.showRequest(this._organizationId())
        : undefined,
  );

  private readonly _invoice = httpResource<LaravelDefaultResponse<InvoiceShowResponse>>(() =>
    this._organizationId() && this._invoiceId()
      ? this._documentService.invoiceRequest(this._organizationId(), this._invoiceId())
      : undefined,
  );

  readonly organizationId = computed<string>(() => this._organizationId());

  readonly organizationName = computed<string>(
    () => this._organizationService.toDetail(this._resourceState.valueOf(this._organization))?.displayName ?? '',
  );

  readonly invoice = computed<InvoiceDetail | null>(() =>
    this._documentService.toInvoiceDetail(this._resourceState.valueOf(this._invoice)),
  );

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._invoice.status()),
  );

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly canSend = computed<boolean>(
    () => this._operatorFacade.atLeast(ROLE_DOCUMENT_SEND) && !this._isMutating(),
  );

  readonly canMoveMoney = computed<boolean>(
    () => this._operatorFacade.atLeast(ROLE_MONEY_OUT) && !this._isMutating(),
  );

  load(organizationId: string, invoiceId: string): void {
    this._organizationId.set(organizationId);
    this._invoiceId.set(invoiceId);
  }

  reload(): void {
    this._invoice.reload();
  }

  openDocument(): void {
    this._documentService
      .documentUrl(this._organizationId(), this._invoiceId())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (url) => this._externalLink.openInNewTab(url),
        error: (error) => this._failMutation(error),
      });
  }

  resend(reason: string): void {
    if (!this.canSend()) {
      return;
    }

    this._isMutating.set(true);

    this._documentService
      .resend(this._organizationId(), this._invoiceId(), reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(INVOICE_RESENT_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  markUncollectible(reason: string): void {
    if (!this.canMoveMoney()) {
      return;
    }

    this._isMutating.set(true);

    this._documentService
      .markUncollectible(this._organizationId(), this._invoiceId(), reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(INVOICE_UNCOLLECTIBLE_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  issueCreditNote(payload: CreditNotePayload): void {
    if (!this.canMoveMoney()) {
      return;
    }

    this._isMutating.set(true);

    this._documentService
      .issueCreditNote(this._organizationId(), this._invoiceId(), payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result) =>
          this._afterWrite(
            result.already_recorded
              ? CREDIT_NOTE_ALREADY_RECORDED_MESSAGE
              : CREDIT_NOTE_CREATED_MESSAGE,
          ),
        error: (error) => this._failMutation(error),
      });
  }

  private _afterWrite(message: string): void {
    this._isMutating.set(false);
    this._invoice.reload();
    this._msgService.showSnackBar(message, { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
